/* eslint-disable camelcase */
import { Button, CheckboxInput, useForm } from "@rocketmakers/armstrong";
import * as React from "react";
import { useParams } from "react-router-dom";
import {
	DeviceType,
	HeatingZoneID,
	PhysicalLocationID,
	RoomSensorNovos3,
	RoomSensorRegin,
	RoomSensorWrf04,
	RuleActionType,
	RuleCategory,
} from "../../../../shared/api-clients/_api";
import { useRoutePaths } from "../../../../shared/context/paths";
import { DEFAULT_SENSOR_LOCATION } from "../../../../shared/helpers/configurationOptionGenerator";
import { enumToArray } from "../../../../shared/helpers/enumHelpers";
import { InputValidator } from "../../../../shared/hooks/ValidationTypes";
import { useValidatedForm } from "../../../../shared/hooks/formValidationHook";
import { BackButton } from "../../components/buttons/back-button";
import { SelectInstallLocation } from "../../components/select-install-location";
import { Spinner } from "../../components/spinner/spinner";
import { useBeeConfig } from "../../hooks/beeConfig";
import { routePaths } from "../../routes";
import { SensorDescriptor } from "./sensor-descriptor";
import { SensorDipswitches } from "./sensor-dipswitches";

export const DEFAULT_SENSOR_ADDRESS = 3;
export const DEFAULT_SENSOR_CHANNEL = "C1";
export const DEFAULT_SENSOR_DIP_SWITCH_POSITION = 0;

interface IThermostatSensorForm {
	location: PhysicalLocationID;
}

export const validators: InputValidator<IThermostatSensorForm>[] = [
	{
		key: "location",
		validators: [
			{
				validate: (value) => enumToArray(HeatingZoneID).includes(value),
				message: "Must be a valid location",
			},
		],
	},
];

export const configurationChecks = ["I confirm I've checked all of the above"];

const initialState: IThermostatSensorForm = {
	location: DEFAULT_SENSOR_LOCATION,
};

export const ConfigureTemperatureSensorView: React.FunctionComponent = () => {
	const { deviceType } = useParams<{ deviceType: DeviceType.WRF04 | DeviceType.NOVOS3 | DeviceType.REGIN }>();
	const { createDevice, editDevice, getDevicesByType, devices, loading } = useBeeConfig();
	const { existingSensor, initState } = React.useMemo(() => {
		// currently there is only expected to be one sensor
		const [sensor] = getDevicesByType<RoomSensorWrf04 | RoomSensorNovos3 | RoomSensorRegin>([
			DeviceType.WRF04,
			DeviceType.REGIN,
			DeviceType.NOVOS3,
		]);
		return {
			existingSensor: sensor,
			initState: sensor ? { location: sensor.params.install_location } : initialState,
		};
	}, [devices]);

	const { DataForm, bind, dataBinder } = useForm<IThermostatSensorForm>(initState);
	const { setTouched, formIsValid, validationErrors } = useValidatedForm<IThermostatSensorForm>(validators, dataBinder);
	const { push } = useRoutePaths();

	const [checklist, updateChecklist] = React.useState(configurationChecks.map(() => !!existingSensor));

	const hasCompletedChecks = checklist.every((value) => value === true);

	React.useEffect(() => {
		if (existingSensor) updateChecklist(configurationChecks.map(() => true));
	}, [existingSensor]);

	const handleContinue = () => {
		const selectedLocation = dataBinder.getValue("location");

		if (existingSensor) {
			editDevice(existingSensor, {
				...existingSensor,
				deviceType,
				params: {
					...existingSensor.params,
					install_location: selectedLocation,
				},
			});
		} else {
			const temperatureSensor: RoomSensorWrf04 | RoomSensorNovos3 | RoomSensorRegin = {
				deviceType,
				params: {
					address: DEFAULT_SENSOR_ADDRESS,
					install_location: selectedLocation,
					channel: DEFAULT_SENSOR_CHANNEL,
					zone_id: null,
					dip_switch_position: DEFAULT_SENSOR_DIP_SWITCH_POSITION,
					category: RuleCategory.TEMPERATURE,
					action_type: RuleActionType.MONITORING,
					primary_zone_temperature_sensor: 0,
				},
			};

			createDevice(temperatureSensor);
		}

		push(routePaths.heatingSystem.root);
	};

	return (
		<DataForm validationResults={validationErrors} validationMode="both">
			{loading && <Spinner />}
			<BackButton />
			<SensorDescriptor deviceType={deviceType} />

			<SelectInstallLocation
				{...bind.select("location")}
				label="Where is the device installed?"
				data-qa="temp-sensor-location"
				onBlur={setTouched}
			/>

			<SensorDipswitches deviceType={deviceType} />

			<div>
				{configurationChecks.map((message, idx) => (
					<CheckboxInput
						// rome-ignore lint/suspicious/noArrayIndexKey: <explanation>
						key={idx}
						labelContent={message}
						checked={checklist[idx]}
						data-qa={`temp-sensor-check-${idx}`}
						onChange={(e) => updateChecklist(checklist.map((value, i) => (idx === i ? e.target.checked : value)))}
					/>
				))}
			</div>

			<div className="row">
				<Button
					className="continue-button btn-full btn-large"
					disabled={!formIsValid || !hasCompletedChecks}
					onClick={handleContinue}
					data-qa="continue-button"
				>
					Continue
				</Button>
			</div>
		</DataForm>
	);
};
